import PropTypes from 'prop-types';
import types from 'config/types/content';
import positions from 'config/images';
import languages from 'config/types/language';
import values from 'lodash/values';
import omit from 'lodash/fp/omit';

const idType = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
]).isRequired;

export const POSITIONS = PropTypes.oneOf(Object.keys(positions));
export const LANGUAGES = PropTypes.oneOf(values(languages));
// TODO: differentiate locales (can be en-US) and editions (can't be en-US)

export const SOCIAL_SHARE_SHAPE = PropTypes.shape({
  link: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  titleAndLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  description: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
});

export const SOCIAL_LINKS = {
  facebook: PropTypes.string,
  twitter: PropTypes.string,
  googlePlus: PropTypes.string,
  youtube: PropTypes.string,
};

export const TAG = {
  id: idType,
  label: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
};

export const TAG_SHAPE = PropTypes.shape(TAG);

export const COAUTHOR_SHAPE = {
  id: idType,
  absolutePath: PropTypes.string.isRequired,
  relativePath: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  description: PropTypes.string.isRequired,
  slug: PropTypes.string,
  avatarUrl: PropTypes.string.isRequired,
};

export const CONTENT_ITEM = {
  id: idType,
  count: PropTypes.number,
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  modified: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string.isRequired,
  coauthors: PropTypes.oneOfType([
    PropTypes.arrayOf(COAUTHOR_SHAPE),
    PropTypes.array,
  ]),
  metadata: PropTypes.shape({
    type: PropTypes.oneOfType([
      PropTypes.oneOf(values(types)),
      PropTypes.bool,
    ]).isRequired,
    canonicalUrl: PropTypes.string,
    languageHreflang: PropTypes.array,
    headerMeta: PropTypes.array,
  }).isRequired,
  path: PropTypes.string.isRequired,
};

export const POST = {
  ...CONTENT_ITEM,
  content: PropTypes.string.isRequired,
  excerpt: PropTypes.string.isRequired,
  metadata: PropTypes.shape({
    featuredImageUrl: PropTypes.string.isRequired,
    morePosts: PropTypes.shape({
      labels: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
};

export const PAGE = {
  ...omit('author', CONTENT_ITEM), // Pages don't ever need to display authors
  metadata: PropTypes.shape({
    submenu: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }),
};

export const LANDING_PAGE = {
  item: PropTypes.shape(CONTENT_ITEM).isRequired,
  relatedItems: PropTypes.arrayOf(
    PropTypes.shape(CONTENT_ITEM),
  ).isRequired,
  loadingMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    hasMore: PropTypes.bool.isRequired,
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
  }).isRequired,
};

export const CONTENT_ARCHIVE = {
  loadingMore: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape(CONTENT_ITEM)).isRequired,
  onLoadMore: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    hasMore: PropTypes.bool.isRequired,
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    paginationLinks: PropTypes.array.isRequired,
  }).isRequired,
  isInProductMagazine: PropTypes.bool,
};

export const MENU = {
  id: PropTypes.number,
  label: PropTypes.string,
  fullUrl: PropTypes.string,
  relativeUrl: PropTypes.string,
  postType: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
};

export const FILTER_CONTROL = {
  label: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object,
  ]).isRequired,
  defaultLabel: PropTypes.string,
  name: PropTypes.string.isRequired,
  selected: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export const LANGUAGE = {
  bgColor: PropTypes.string,
  flag: PropTypes.string.isRequired,
  isCircle: PropTypes.bool,
  text: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  url: PropTypes.string.isRequired,
};
